import {
  ComponentMultiStyleConfig,
  FormControl,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useCallback, useMemo } from "react";
import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";
import { hasValue } from "../../../../lib/util/common-util";
import { CheckboxContent, CMFormInputBaseCheckbox } from "../../../control";
import { CMButtonFormCommentProps } from "../../comment";
import { CMFormControlHeader } from "../CMFormControlHeader/CMFormControlHeader";
import {
  ChangeBadgeProperties,
  messageId,
  ValueObjectMetaEnum,
} from "../../../../lib";
import { useMessageDictionary } from "../../../../hooks/use-message-dictionary";
import { FormRequiredLabelProps } from "../../label";

export type CMFormInputCheckboxProps<T extends [string, ...string[]]> = {
  editMode?: "editable" | "disabled" | "readOnly";
  size?: "sm" | "md" | "lg";

  /**
   * trueの場合、ヘッダなしとなる。
   * この場合、changeBadgePropertiesやcommentButtonPropsが存在しても、変更やコメントを扱わない。
   */
  noHeader?: boolean;

  label?: string;
  /**
   * チェックボックスの表示方向
   */
  direction?: "row" | "column";

  /**
   * 値。
   */
  values?: T[number][];

  /**
   * 値オブジェクトメタデータ
   */
  valueObjectMeta: ValueObjectMetaEnum<T>;

  /**
   * 必須マークの設定。
   * この項目が設定されている場合は必須ラベルを表示する。
   */
  requiredLabelProps?: FormRequiredLabelProps;

  /**
   * 変更バッジのprops。
   * 値があれば、このコントロールは差分モード(差分があれば変更バッジとオレンジ枠を表示するモード)となる。
   */
  changeBadgeProperties?: ChangeBadgeProperties<T[number][]>;

  /**
   * コメントボタンのprops。
   * 定義されている場合、コメントボタンを表示する。
   */
  commentButtonProps?: CMButtonFormCommentProps;

  onChange?: (selectedValues: T[number][]) => void;

  // このコンポーネントではバリデーションを行わないため、onValidateは提供しない。
} & ComponentStyleProps;

/**
 * Checkboxコンポーネント.
 *
 * 差分モードでは、変更後の選択肢に枠を表示する。
 * 選択値は配列で設定する。
 * エラーモード非対応。
 */
export const CMFormInputCheckbox = <T extends [string, ...string[]]>({
  editMode = "editable",
  size = "md",

  noHeader = false,

  label,
  direction = "column",

  values = [],
  valueObjectMeta,

  requiredLabelProps,

  changeBadgeProperties,

  commentButtonProps,

  onChange,

  sx,
  ...rest
}: CMFormInputCheckboxProps<T>) => {
  const styles = useMultiStyleConfig("CMFormInputCheckboxStyles");
  const dict = useMessageDictionary();

  // // 現在の値
  // const [value, setValue] = useState<string[]>(initialValues);

  // metaデータから取得した値をcontentの形に変換する
  const content: CheckboxContent = useMemo(
    () => ({
      options: valueObjectMeta.values.map((v) => ({
        label: dict(messageId(valueObjectMeta.dict[v])) ?? "",
        value: v,
      })),
    }),
    [valueObjectMeta, dict],
  );

  /**
   * 差分モードならtrue。
   */
  const isDiffMode = useMemo(
    () => hasValue(changeBadgeProperties),
    [changeBadgeProperties],
  );

  const handleChange = (values: string[]) => {
    const validValues = values
      .map((v) => {
        const result = valueObjectMeta.validate(v);
        if (result.state !== "ok") return null;
        return result.value;
      })
      .filter(hasValue);

    onChange?.(validValues);
  };

  const toDisplayValue = useCallback(
    (values: T[number][]) => {
      return values
        .map((v) => {
          const option = content.options.find((o) => o.value === v);
          return option?.label;
        })
        .filter(hasValue)
        .join(", ");
    },
    [content.options],
  );

  return (
    <FormControl sx={{ ...styles.container, ...sx }} {...rest}>
      {!noHeader && (
        <CMFormControlHeader<T[number][]>
          label={label}
          value={values}
          requiredLabelProps={requiredLabelProps}
          changeBadgeProperties={changeBadgeProperties}
          toDisplayValue={toDisplayValue}
          commentButtonProps={commentButtonProps}
          sx={styles.header}
        />
      )}
      <CMFormInputBaseCheckbox
        editMode={editMode}
        variant={isDiffMode ? "diff" : "default"}
        size={size}
        shrinkOnReadOnly={true}
        content={content}
        direction={direction}
        values={values}
        changedFrom={changeBadgeProperties?.changedFrom}
        onChange={handleChange}
        sx={styles.input}
      />
    </FormControl>
  );
};

export const CMFormInputCheckboxStyles: ComponentMultiStyleConfig = {
  parts: ["container", "header", "input", "error"],
  baseStyle: {
    container: {},
    header: {
      mb: "2px",
    },
    input: {},
    error: {
      mt: "4px",
    },
  },
};
